import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, clipFrame, clipOutroFade, combineSceneFragments, makeTl, typeSize, weights, withAlpha,
  colors as themeColors, escapeHtml,
} from "../../reel/sceneShared.js";

/**
 * Handwritten port — uses SVG <text> with stroke-dasharray drawing animation.
 */
export const handwritten: SceneBuilder = ({ ctx, clipPrefix, inputs, clipDurationSec, clipStartSec }) => {
  const text = inputs.text ?? "Made for locals.";
  const sub = inputs.subtext ?? "";
  const accent = accentFor(inputs);
  const md = typeSize("md", ctx.format);
  const lg = typeSize("lg", ctx.format);

  const t = makeTl(clipStartSec);
  const textId = `${clipPrefix}-txt`;
  const underlineId = `${clipPrefix}-ul`;
  const subId = `${clipPrefix}-sub`;

  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "cinematic", accent });

  const inner = `<svg viewBox="0 0 1200 280" preserveAspectRatio="xMidYMid meet" style="width:${ctx.format === "9:16" ? "92%" : "78%"};max-width:1500px;overflow:visible;">
  <defs>
    <linearGradient id="${clipPrefix}-grad" x1="0%" y1="0%" x2="100%" y2="0%">
      <stop offset="0%" stop-color="${accent}" />
      <stop offset="100%" stop-color="${themeColors.accent}" />
    </linearGradient>
  </defs>
  <text id="${textId}" x="50%" y="55%" text-anchor="middle" dominant-baseline="middle" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${lg}px;letter-spacing:-3.5px;fill:${withAlpha(themeColors.fg, 0)};stroke:url(#${clipPrefix}-grad);stroke-width:2.2;stroke-linejoin:round;stroke-linecap:round;stroke-dasharray:4000;stroke-dashoffset:4000;">${escapeHtml(text)}</text>
  <line id="${underlineId}" x1="20%" y1="78%" x2="20%" y2="78%" stroke="${accent}" stroke-width="6" stroke-linecap="round" style="filter:drop-shadow(0 0 14px ${withAlpha(accent, 0.7)});" />
</svg>
${sub ? `<div id="${subId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.semibold};font-size:${md * 0.55}px;color:${withAlpha(themeColors.fg, 0.85)};text-align:center;letter-spacing:-0.4px;line-height:1.05;max-width:${ctx.format === "9:16" ? 760 : 900}px;">${escapeHtml(sub)}</div>` : ""}`;

  const html = clipFrame({ clipPrefix, clipStartSec, clipDurationSec, inner, gapPx: 64 });

  const drawDurSec = Math.max(28, Math.min(48, text.length * 2)) / 30;
  const tweens: string[] = [
    t.to(`#${textId}`, { strokeDashoffset: 0, duration: drawDurSec, ease: "raw:\"power2.out\"" }, 4 / 30),
    t.to(`#${textId}`, { fill: themeColors.fg, duration: 16 / 30, ease: "raw:\"power1.out\"" }, 4 / 30 + drawDurSec * 0.7),
    t.to(`#${underlineId}`, { attr: { x2: "80%" }, duration: 18 / 30, ease: "raw:\"power2.out\"" }, 4 / 30 + drawDurSec),
  ];
  if (sub) tweens.push(t.to(`#${subId}`, { opacity: 1, y: 0, duration: 0.6, ease: "raw:\"power1.out\"" }, 4 / 30 + drawDurSec + 8 / 30));
  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec));

  const init = sub ? `gsap.set("#${subId}", { opacity: 0, y: 14 });` : "";

  return combineSceneFragments([backdrop, { html, init, tweens }]);
};
